import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';
import { StockDTO } from '../../../../api/api';
import useTableInstance from '../../../../hooks/useTableInstance';
import Checkbox from '../../../../components/checkbox/Checkbox';
import { SearchColumnFilter } from '../../../../blocks/table/TableFilterUtils';
import { GoodsData } from '../../../../models/Goods';
import IconButton from '../../../../components/icon-button/IconButton';
import { CameraIcon } from '../../../../assets/icons/CameraIcon';

export interface StockData {
  name?: string;
  minMax?: string;
  itemId?: string;
  selected?: boolean;
  available?: string;
  minOnHand?: string;
  pictureLinks?: string[];
}

export const useStockTable = (
  stocks: (StockDTO & { selected?: boolean })[],
  stockAmountColumnName: string,
  selectItem: (id: string, value: boolean) => void,
  showDetails?: (goodsData: GoodsData) => void
) => {
  const data = useMemo(() => {
    if (!stocks.length) return [];

    return stocks.map((stock) => {
      const idSuffix = stock.product?.batchNumber || stock.product?.serialNumber;
      return {
        name: stock.product?.name + (idSuffix ? ` (${idSuffix})` : ''),
        minMax: `${stock.minOnHand}/${stock.maxOnHand}`,
        minOnHand: stock.minOnHand,
        itemId: stock.product?.itemId,
        selected: stock.selected,
        available: stock.available,
        pictureLinks: stock.product?.pictureLinks
      } as StockData;
    });
  }, [stocks]);

  const columns = useMemo(() => {
    const _columns = [
      {
        Header: '',
        accessor: 'selected',
        width: 20,
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<StockData>) => {
          const { cell } = cellProps;
          return (
            <Checkbox
              checked={cell.row.original.selected}
              onChange={(e) => selectItem(cell.row.original.itemId ?? '', e.target.checked)}
              disabled={(cell.row.original.available ?? 0) <= (cell.row.original.minOnHand ?? 0)}
            />
          );
        }
      },
      {
        Header: 'Varenummer',
        accessor: 'itemId',
        width: 80,
        minWidth: 80,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Navn',
        accessor: 'name',
        width: 150,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: stockAmountColumnName,
        accessor: 'available',
        width: 50,
        minWidth: 50,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Min/Max',
        accessor: 'minMax',
        width: 75,
        minWidth: 75,
        Filter: SearchColumnFilter,
        filter: 'text'
      }
    ] as Column<StockData>[];

    // Only show 'product-details' column if showDetails callback is set
    showDetails &&
      _columns.splice(0, 0, {
        Header: '',
        accessor: 'pictureLinks',
        width: 30,
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<GoodsData>) => {
          const { cell } = cellProps;
          const pictureLinks = cell.row.original.pictureLinks ?? [];
          return (
            <IconButton
              variant="outlined"
              disabled={pictureLinks?.length === 0 || !pictureLinks}
              onClick={() => {
                const data: StockData = cell.row.original;
                showDetails({
                  goodsName: data.name,
                  goodsId: data.itemId,
                  pictureLinks: data.pictureLinks
                } as StockData);
              }}
            >
              <CameraIcon size="20px" />
            </IconButton>
          );
        }
      });

    return _columns;
  }, [data]);

  const tableInstance = useTableInstance<StockData>(data, columns);

  return { tableInstance };
};
